import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  CircleAlert,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Unplug,
  Webhook,
} from "lucide-react";
import {
  ApiError,
  connectLodgify,
  disconnectLodgify,
  getLodgifyConnection,
  verifyLodgifyConnection,
} from "../api";

function readableError(error: unknown): string {
  if (!(error instanceof ApiError)) return "The connection could not be completed.";
  return (
    {
      invalid_credentials: "Lodgify rejected this API key. Check the key and try again.",
      rate_limited: "Lodgify is temporarily limiting requests. Try again shortly.",
      provider_subscription_failed:
        "The key is valid, but Lodgify could not create every webhook. Retry to clean up and continue.",
      provider_cleanup_failed:
        "An earlier webhook could not be removed. Review the Lodgify webhook list, then retry.",
      provider_unsubscribe_failed:
        "Lodgify could not remove every webhook, so Yesward kept the encrypted credential for a safe retry.",
    }[error.code] ?? "The connection could not be completed. Try again."
  );
}

export function LodgifyIntegrationPage() {
  const { organizationId, siteId } = useParams({
    from: "/organizations/$organizationId/sites/$siteId/integrations/lodgify",
  });
  const queryClient = useQueryClient();
  const queryKey = ["lodgify", organizationId, siteId];
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [disconnectArmed, setDisconnectArmed] = useState(false);
  const connectionQuery = useQuery({
    queryKey,
    queryFn: () => getLodgifyConnection(organizationId, siteId),
  });
  const connectMutation = useMutation({
    mutationFn: () => connectLodgify(organizationId, siteId, apiKey),
    onSuccess: async () => {
      setApiKey("");
      setShowKey(false);
      await queryClient.invalidateQueries({ queryKey });
    },
  });
  const verifyMutation = useMutation({
    mutationFn: () => verifyLodgifyConnection(organizationId, siteId),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey }),
  });
  const disconnectMutation = useMutation({
    mutationFn: () => disconnectLodgify(organizationId, siteId),
    onSuccess: async () => {
      setDisconnectArmed(false);
      await queryClient.invalidateQueries({ queryKey });
    },
  });

  const connection = connectionQuery.data?.connection;
  const connected = connection?.status === "connected";
  const working =
    connectMutation.isPending || verifyMutation.isPending || disconnectMutation.isPending;
  const mutationError =
    connectMutation.error ?? verifyMutation.error ?? disconnectMutation.error;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    connectMutation.mutate();
  }

  const steps = [
    {
      number: "01",
      label: "Credentials",
      detail: connected ? "Encrypted · access verified" : "Owner authorization required",
      done: connected,
    },
    {
      number: "02",
      label: "Properties",
      detail: connected
        ? `${connection.properties.length} discovered`
        : "Discovered after verification",
      done: Boolean(connected && connection.properties.length),
    },
    {
      number: "03",
      label: "Lifecycle receipts",
      detail: connected ? `${connection.events.length} signed webhooks` : "Created by Yesward",
      done: Boolean(connected && connection.events.length),
    },
    {
      number: "04",
      label: "Profit model",
      detail: "Owner approval required",
      done: false,
    },
  ];

  return (
    <div className="page-frame integration-page">
      <Link className="back-link" to="/sites">
        <ArrowLeft size={14} aria-hidden="true" /> Websites
      </Link>
      <section className="page-heading integration-heading">
        <div>
          <p className="eyebrow">OUTCOME SOURCE · LODGIFY</p>
          <h1>Connect bookings to profit evidence.</h1>
          <p className="page-intro">
            Yesward verifies account access, discovers properties and subscribes
            only to booking lifecycle events. Guest records never enter Yesward.
          </p>
        </div>
        <span className={`connection-state ${connected ? "is-connected" : ""}`}>
          <i /> {connected ? "CONNECTED" : connection?.status === "error" ? "ATTENTION" : "NOT CONNECTED"}
        </span>
      </section>

      <section className="integration-sequence" aria-label="Connection sequence">
        {steps.map((step) => (
          <div className={step.done ? "sequence-step is-done" : "sequence-step"} key={step.number}>
            <span>{step.done ? <Check size={13} /> : step.number}</span>
            <div>
              <b>{step.label}</b>
              <small>{step.detail}</small>
            </div>
          </div>
        ))}
      </section>

      {connectionQuery.isLoading ? (
        <section className="integration-loading">
          <LoaderCircle size={18} className="spin" /> Reading connection state…
        </section>
      ) : connected && connection ? (
        <div className="integration-grid">
          <section className="connection-panel">
            <div className="section-heading">
              <div>
                <p className="eyebrow">VERIFIED ACCESS</p>
                <h2>{connection.site.name}</h2>
              </div>
              <span className="status-chip teal">PRODUCTION</span>
            </div>
            <div className="connection-receipt">
              <div>
                <span>Provider</span>
                <b>Lodgify Public API</b>
              </div>
              <div>
                <span>Last verified</span>
                <b>
                  {connection.lastVerifiedAt
                    ? new Intl.DateTimeFormat("en-GB", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }).format(connection.lastVerifiedAt)
                    : "Pending"}
                </b>
              </div>
              <div>
                <span>Credential</span>
                <b><LockKeyhole size={13} /> Encrypted · not retrievable</b>
              </div>
            </div>
            <div className="connection-actions">
              <button
                className="secondary-action"
                type="button"
                onClick={() => verifyMutation.mutate()}
                disabled={working}
              >
                <RefreshCw size={14} /> Verify now
              </button>
              {!disconnectArmed ? (
                <button
                  className="quiet-danger"
                  type="button"
                  onClick={() => setDisconnectArmed(true)}
                  disabled={working}
                >
                  <Unplug size={14} /> Disconnect
                </button>
              ) : (
                <div className="disconnect-confirm">
                  <span>Remove all Yesward webhooks?</span>
                  <button type="button" onClick={() => disconnectMutation.mutate()} disabled={working}>
                    Confirm
                  </button>
                  <button type="button" onClick={() => setDisconnectArmed(false)}>Cancel</button>
                </div>
              )}
            </div>
          </section>

          <section className="property-panel">
            <div className="section-heading">
              <div>
                <p className="eyebrow">DISCOVERED INVENTORY</p>
                <h2>{connection.properties.length} properties</h2>
              </div>
              <span className="status-chip amber">MAPPING NEXT</span>
            </div>
            <div className="property-list">
              {connection.properties.map((property) => (
                <div key={property.id}>
                  <span>
                    <b>{property.name || "Unnamed property"}</b>
                    <small>LOD-{property.id}</small>
                  </span>
                  <em>UNMAPPED</em>
                </div>
              ))}
            </div>
            <p className="panel-note">
              <ShieldCheck size={15} /> Only property IDs and names are retained for mapping.
            </p>
          </section>
        </div>
      ) : (
        <div className="integration-grid">
          <section className="connection-panel">
            <div className="section-heading">
              <div>
                <p className="eyebrow">OWNER AUTHORIZATION</p>
                <h2>Enter a Lodgify API key</h2>
              </div>
              <KeyRound size={19} aria-hidden="true" />
            </div>
            <form className="credential-form" onSubmit={submit}>
              <label htmlFor="lodgify-api-key">Lodgify API key</label>
              <div className="secret-input">
                <input
                  id="lodgify-api-key"
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowKey((value) => !value)}
                  aria-label={showKey ? "Hide API key" : "Show API key"}
                >
                  {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <div className="credential-help">
                <span>The key is sent once over TLS, then encrypted before storage.</span>
                <a href="https://docs.lodgify.com/docs/getting-started" target="_blank" rel="noreferrer">
                  Find your key <ExternalLink size={12} />
                </a>
              </div>
              {mutationError ? (
                <p className="form-error" role="alert">
                  <CircleAlert size={14} /> {readableError(mutationError)}
                </p>
              ) : null}
              <button className="primary-action" type="submit" disabled={!apiKey.trim() || working}>
                {connectMutation.isPending ? (
                  <><LoaderCircle className="spin" size={15} /> Verifying and subscribing…</>
                ) : (
                  <><Webhook size={15} /> Connect Lodgify</>
                )}
              </button>
            </form>
          </section>

          <aside className="data-boundary-panel">
            <p className="eyebrow">DATA BOUNDARY</p>
            <h2>Commercial facts in. Guest data out.</h2>
            <div className="boundary-list">
              <div><Check size={13} /><span><b>Retained</b><small>Property identity, booking state, value and signed journey reference</small></span></div>
              <div><Check size={13} /><span><b>Authenticated</b><small>Every webhook is verified against its creation-time secret</small></span></div>
              <div><Unplug size={13} /><span><b>Discarded</b><small>Guest name, email, phone, notes, IP address and raw payload</small></span></div>
            </div>
          </aside>
        </div>
      )}

      {connected ? (
        <section className="profit-gate">
          <ShieldCheck size={18} />
          <div>
            <p className="eyebrow">NEXT CONTROL GATE</p>
            <h2>Map properties and approve their economics.</h2>
            <p>Yesward will not claim gross profit until each revenue basis and variable-cost model is owner-approved.</p>
          </div>
          <span>PROFIT PROOF LOCKED</span>
        </section>
      ) : null}
    </div>
  );
}
